// Airbnb-style salon card for the public marketplace grid (SearchPage).
//
// Anatomy:
//   - Image: square aspect, rounded-2xl, sits flush with the text below
//     it. No wrapping panel with border or shadow — the image is the
//     only chromed element, the rest is naked text on the page.
//   - Heart toggle: absolute-positioned over the top-right corner of
//     the image, semi-transparent white pill so it stays legible on
//     any photo.
//   - Text rows below: name + rating on row 1, city / address / price
//     stacked tight underneath. No CTA button — the entire card is the
//     CTA via the outer <Link>.
//
// Differs from SalonCard's old detailed variant (which still lived in
// a bordered panel with a gradient "Vezi disponibilitate" button at
// the bottom) by stripping the panel chrome and the explicit button
// in favor of the cleaner image-driven listing UX the modern
// marketplaces converged on.

import { Link } from 'react-router-dom';
import { FiStar, FiHeart } from 'react-icons/fi';
import FounderBadge from '../ui/FounderBadge';
import { formatRoNumber } from '../../utils/formatNumber';

export default function MarketplaceCard({
  salon,
  isFavorite = false,
  onToggleFavorite,
  // Image aspect ratio. Square in the marketplace grid; a shorter landscape
  // (e.g. 'aspect-[3/2]') in the desktop split so the cards stay compact next
  // to the map, like Fresha.
  imageAspect = 'aspect-square',
}) {
  if (!salon) return null;

  return (
    // Outer wrapper carries the `group` class (so the image scale
    // animation reads hover state from anywhere on the card) and
    // `relative` (so the heart button can absolute-position against
    // it). The favorite toggle sits as a SIBLING of the Link rather
    // than a descendant — nesting an interactive <button> inside an
    // <a> is invalid HTML and screen readers / browsers handle it
    // inconsistently (some announce the inner button as part of the
    // link's accessible name, some swallow the click). Lifting it out
    // keeps the two affordances independent.
    <div className="group relative block">
      <Link to={`/salon/${salon.slug}`} className="block">
        <div className={`relative ${imageAspect} overflow-hidden rounded-2xl bg-cream-200`}>
          {salon.image ? (
            <img
              src={salon.image}
              // Empty alt: the salon name already lives in the <h3>
              // inside this same <Link>, so a non-empty alt would
              // make a screen reader announce the name twice when the
              // link receives focus. The image is decorative-in-
              // context (it's the visual half of a named link), not
              // standalone content that needs its own description.
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            // Neutral cream fallback when the salon hasn't uploaded a
            // cover. Matches the warm-editorial minimalism the rest of
            // the page leans into — a colored gradient here would read
            // as visual noise against the otherwise calm cream page.
            //
            // aria-hidden on the wrapper because both the emoji and
            // the inner name copy are decorative duplicates of the
            // <h3> below. Without it, the link's accessible name
            // becomes "🐾 Bubble Pet Bubble Pet" instead of just
            // "Bubble Pet".
            <div
              aria-hidden="true"
              className="flex h-full w-full items-center justify-center bg-cream-200"
            >
              <div className="text-center">
                <span className="text-5xl opacity-40">🐾</span>
                <p className="mt-2 px-3 text-sm font-medium text-ink-500">
                  {salon.name}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Text block — no padding wrapper so the lines sit flush with
            the image edge on the left. Type scale stays tight at the
            ~150px width mobile 2-col gives us, then bumps a step on lg
            where the 3-col grid makes each card much wider. */}
        <div className="mt-2 space-y-0.5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-ink lg:text-base">
              {salon.name}
            </h3>
            {salon.reviewCount > 0 && (
              <span className="flex flex-shrink-0 items-center gap-1 text-xs text-ink lg:text-sm">
                <FiStar className="h-3 w-3 fill-yellow-500 text-yellow-500 lg:h-3.5 lg:w-3.5" />
                <span className="font-medium">{salon.rating}</span>
                <span className="text-ink-500">({formatRoNumber(salon.reviewCount)})</span>
              </span>
            )}
          </div>

          {salon.isFounder && (
            // V2 (owner's pick): seal + label on its own line, so the launch
            // cohort reads as "fondator" right in the list — the seal alone
            // scanned as generic "verified". primary-700 text per the mock.
            <p className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 lg:text-xs">
              <FounderBadge className="h-3.5 w-3.5" />
              Salon fondator
            </p>
          )}

          {salon.city && (
            <p className="truncate text-xs text-ink-500 lg:text-sm">{salon.city}</p>
          )}

          {salon.address && (
            <p className="truncate text-xs text-ink-500 lg:text-sm">{salon.address}</p>
          )}
        </div>
      </Link>

      {onToggleFavorite && (
        <button
          type="button"
          onClick={(e) => {
            // Sibling-of-Link layout means the click won't bubble
            // through to the anchor on its own, but we keep the
            // stop-propagation pair as belt-and-braces against any
            // future wrapper that re-introduces an outer interactive
            // element.
            e.preventDefault();
            e.stopPropagation();
            onToggleFavorite(salon.id);
          }}
          aria-label={
            isFavorite
              ? `Scoate ${salon.name} din favorite`
              : `Adaugă ${salon.name} la favorite`
          }
          aria-pressed={isFavorite}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-cream-50/85 backdrop-blur transition hover:scale-110 hover:bg-cream-50"
        >
          <FiHeart
            className={`h-4 w-4 transition-colors ${
              isFavorite ? 'fill-current text-red-500' : 'text-ink'
            }`}
          />
        </button>
      )}
    </div>
  );
}
